import type { GrantablePermission, OrderResponse } from '@palaccount/contracts';

import type { LifecycleStep } from '@/components/document-list/types';

/** §5d — the five order-to-cash stages, in order. */
export const STAGES = ['quotation', 'sales_order', 'delivery', 'invoice', 'payment'] as const;

export type StepState = 'done' | 'current' | 'skipped' | 'pending';

/**
 * D-09's remainder — a stage with NO document yet that is the next sensible
 * act is a BUTTON, not a label. `navigate` opens an editor pre-filled from
 * the order; `createInvoice` is the page's own Create-invoice act (one POST,
 * then the new invoice opens). Each names the permission that gates it and
 * the `perm.cannot.*` verb phrase `<Gated>` greys it with.
 */
export type StepAction =
  | {
      kind: 'navigate';
      to: string;
      permission: GrantablePermission;
      reasonKey: string;
    }
  | { kind: 'createInvoice'; permission: GrantablePermission; reasonKey: string };

export interface Step {
  stage: (typeof STAGES)[number];
  state: StepState;
  /** Where clicking the step goes, when there is a document behind it. */
  href: string | null;
  /** What clicking the step DOES, when there is no document yet; see StepAction. */
  action: StepAction | null;
}

const isZero = (pct: string) => Number(pct) === 0;
const isFull = (pct: string) => Number(pct) >= 100;

/**
 * The stepper, read from FACTS rather than position alone (bugs: Sales Order —
 * "if some stages were skipped, show them grey"). The server's `stage` is
 * still the current step; what changes is the steps BEHIND it:
 *
 *  - Quotation is only "done" when the order really came from a quotation.
 *    An order typed directly skipped it.
 *  - Delivery is "skipped" once the order is past it with nothing delivered
 *    (invoiced or paid without a delivery note).
 *
 * A step that exists as a document links to it — the click that takes the
 * reader to the next document in the chain. A step that does not exist yet
 * but could be made now carries an ACTION instead (D-09):
 *
 *  - Delivery, while the order is confirmed and not fully delivered — a new
 *    delivery note on this order (partial deliveries keep it live until the
 *    last unit leaves; BR-SAL-03-02's remainder is what the editor loads).
 *  - Invoice, under exactly the Create-invoice button's condition
 *    (confirmed / partially delivered / fulfilled, nothing billed yet —
 *    BR-SAL-02-06 hides it on a draft and a cancelled order).
 *  - Payment, once the invoice is POSTED and not yet fully paid — a receipt
 *    voucher for this customer. A draft invoice has nothing to settle.
 */
export function orderSteps(order: OrderResponse): Step[] {
  const current = STAGES.indexOf(order.stage);
  return STAGES.map((stage, index) => {
    let state: StepState =
      index < current ? 'done' : index === current ? 'current' : 'pending';
    if (state === 'done' && stage === 'quotation' && order.sourceQuotationId === null) {
      state = 'skipped';
    }
    if (state === 'done' && stage === 'delivery' && isZero(order.deliveredPct)) {
      state = 'skipped';
    }
    const href =
      stage === 'quotation' && order.sourceQuotationId !== null
        ? `/sales/quotes/${order.sourceQuotationId}`
        : stage === 'invoice' && order.invoiceId !== null
          ? `/sales/${order.invoiceId}`
          : null;
    return { stage, state, href, action: href === null ? stepAction(stage, order) : null };
  });
}

/**
 * The same five steps in the shape the shared `LifecycleStepper` draws. Pure
 * apart from the two things only the page has: its Create-invoice act (one
 * POST, then the new invoice opens) and whether it is busy. The stage name
 * and the act's tooltip are keys — `orders.stage.<stage>` and
 * `orders.stepAct.<stage>` — so the dictionaries stay the single wording.
 */
export function lifecycleSteps(
  order: OrderResponse,
  acts: { createInvoice: () => void; busy: boolean },
): LifecycleStep[] {
  return orderSteps(order).map((step) => ({
    key: step.stage,
    labelKey: `orders.stage.${step.stage}`,
    state: step.state,
    href: step.href ?? undefined,
    action:
      step.action === null
        ? undefined
        : step.action.kind === 'navigate'
          ? { labelKey: `orders.stepAct.${step.stage}`, to: step.action.to }
          : { labelKey: `orders.stepAct.${step.stage}`, onAct: acts.createInvoice, disabled: acts.busy },
    permission:
      step.action === null
        ? undefined
        : { permission: step.action.permission, reasonKey: step.action.reasonKey },
  }));
}

function stepAction(stage: (typeof STAGES)[number], order: OrderResponse): StepAction | null {
  const open = order.status === 'confirmed' || order.status === 'partially_delivered';
  if (stage === 'delivery' && open && !isFull(order.deliveredPct)) {
    return {
      kind: 'navigate',
      to: `/sales/deliveries/new?orderId=${order.id}`,
      permission: 'sales.create',
      reasonKey: 'perm.cannot.createDeliveries',
    };
  }
  if (stage === 'invoice' && (open || order.status === 'fulfilled') && order.invoiceId === null) {
    return { kind: 'createInvoice', permission: 'sales.create', reasonKey: 'perm.cannot.createInvoices' };
  }
  if (
    stage === 'payment' &&
    order.invoiceId !== null &&
    order.invoicePosted &&
    order.stage !== 'payment'
  ) {
    return {
      kind: 'navigate',
      to: `/accounting/receipts/new?partnerId=${order.partnerId}`,
      permission: 'accounting.create',
      reasonKey: 'perm.cannot.createReceiptVouchers',
    };
  }
  return null;
}
